import Seo from "@/components/Seo";
import LeadForm from "@/components/LeadForm";
import { FadeUp, MaskedLines, Stagger, StaggerItem } from "@/components/Reveal";
import { Eyebrow, LuxeButton } from "@/components/shared";

const HERO_IMG = "https://images.unsplash.com/photo-1640109229792-a26a0ee366ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHx8bGl2aW5nJTIwcm9vbXxlbnwwfHx8MTc4ODk2MTAzNXww&ixlib=rb-4.1.0&q=85";

const CHAPTERS = [
  { n: "01", title: "Strategic Pricing", copy: "A disciplined read of the market — comparable sales, active competition, and buyer sentiment — to position your home where demand responds." },
  { n: "02", title: "Luxury Presentation", copy: "Professional staging guidance and editorial-quality photography and film, so your property looks like the life a buyer wants to step into." },
  { n: "03", title: "Digital Exposure", copy: "Sophisticated, targeted marketing that reaches qualified buyers locally, nationally, and internationally — not just impressions, but the right eyes." },
  { n: "04", title: "Expert Negotiation", copy: "Calm, informed, unemotional advocacy that evaluates offers on more than price — terms, certainty, and the credibility of the other side." },
  { n: "05", title: "Transaction Management", copy: "From contract to closing, every detail is tracked and every deadline protected, so the result you negotiated is the result you keep." },
];

const ListWithUs = () => (
  <>
    <Seo
      title="List With Us | Sell Your Luxury Home in DC, MD & VA — Scherif Real Estate"
      siteName="Scherif Real Estate"
      description="Strategic pricing, luxury marketing, expert negotiation, and seamless transaction management. Request a private consultation with Scherif Real Estate."
    />

    <section className="relative flex min-h-[80vh] items-end overflow-hidden bg-obsidian" data-testid="seller-hero">
      <img src={HERO_IMG} alt="Elegant living room — list your home with Scherif Real Estate" className="absolute inset-0 h-full w-full object-cover opacity-80" />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/45 to-obsidian/20" aria-hidden="true" />
      <div className="container-luxe relative z-10 pb-24 pt-40">
        <Eyebrow dark className="mb-8">For Sellers</Eyebrow>
        <h1 className="max-w-4xl font-serif text-[12vw] font-light leading-[1.04] tracking-tight text-ivory sm:text-7xl" data-testid="seller-headline">
          <MaskedLines delay={0.3} lines={["Your Property.", "Our Expertise."]} />
        </h1>
        <FadeUp delay={0.9}>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-ivory/85 sm:text-lg">
            Selling a significant home is a campaign, not an event. Here is how we run yours.
          </p>
          <div className="mt-10">
            <LuxeButton href="#consultation" variant="light" testId="seller-hero-consult-btn">Request a Consultation</LuxeButton>
          </div>
        </FadeUp>
      </div>
    </section>

    <section className="container-luxe py-24 sm:py-36" data-testid="seller-chapters">
      <FadeUp className="mb-16 max-w-2xl">
        <Eyebrow className="mb-4">The Approach</Eyebrow>
        <h2 className="font-serif text-4xl font-light tracking-tight text-charcoal sm:text-5xl">Five Disciplines. One Result.</h2>
      </FadeUp>
      <Stagger className="divide-y divide-charcoal/10 border-y border-charcoal/10">
        {CHAPTERS.map((c) => (
          <StaggerItem key={c.n}>
            <div className="group grid gap-4 py-10 transition-colors duration-500 hover:bg-stone-beige/60 sm:grid-cols-12 sm:items-baseline sm:gap-8 sm:px-6">
              <span className="font-serif text-4xl font-light italic text-champagne sm:col-span-2">{c.n}</span>
              <h3 className="font-serif text-2xl font-light text-charcoal sm:col-span-4 sm:text-3xl">{c.title}</h3>
              <p className="text-sm leading-relaxed text-slate-muted sm:col-span-6">{c.copy}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </section>

    <section id="consultation" className="scroll-mt-28 bg-stone-beige py-24 sm:py-36" data-testid="seller-form-section">
      <div className="container-luxe grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <FadeUp>
            <Eyebrow className="mb-4">Begin the Conversation</Eyebrow>
            <h2 className="font-serif text-4xl font-light leading-[1.1] tracking-tight text-charcoal sm:text-5xl">
              Request a Private Consultation
            </h2>
            <p className="mt-8 max-w-md text-base leading-relaxed text-slate-muted">
              Tell us about your property and your timeline. Every engagement begins with an honest, confidential conversation — no obligation.
            </p>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-charcoal">
              Or call directly —{" "}
              <a href="tel:+12403724804" className="text-champagne link-underline" data-testid="seller-call-link">240-372-4804</a>
            </p>
          </FadeUp>
        </div>
        <FadeUp delay={0.15} className="lg:col-span-7">
          <div className="border border-warm-border bg-white p-6 shadow-[0_20px_60px_rgba(0,0,0,0.05)] sm:p-10">
            <LeadForm type="seller" testIdPrefix="seller-inquiry" />
          </div>
        </FadeUp>
      </div>
    </section>
  </>
);

export default ListWithUs;
